import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatService } from '../services/chatService'
import { userService } from '../services/userService'
import { useAuth } from '../contexts/AuthContext'
import { ChatInput } from './ChatInput'
import { ChatMessage } from './ChatMessage'
import { Avatar } from './Avatar'
import { groupMessagesByDate } from '../utils/chat'
import { formatUserName } from '../utils/name'
import { Message } from '../types/chat'
import './ChatList.css'

type ConversationType = 'team' | 'all' | 'direct'

export function ChatList() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [conversation, setConversation] = useState<{ type: ConversationType; userId?: string }>({ type: 'team' })
  const lastCacheUpdateRef = useRef<Map<string, Message>>(new Map()) // Track last known good state per message ID

  const queryResult = useQuery({
    queryKey: ['chat-messages', conversation.type, conversation.userId],
    queryFn: () => {
      if (conversation.type === 'direct' && conversation.userId) {
        return chatService.getDirectMessages(conversation.userId)
      }
      if (conversation.type === 'team') {
        return chatService.getTeamMessages()
      }
      return chatService.getAll()
    },
    refetchInterval: 3000, // Poll every 3 seconds for new messages
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid race conditions
    enabled: conversation.type !== 'direct' || !!conversation.userId,
  })

  // Merge fetched data with ref to preserve reactions that might not be in backend yet
  const messages = useMemo(() => {
    const fetchedMessages = queryResult.data || []
    
    // Merge fetched data with ref data to preserve reactions
    const mergedMessages = fetchedMessages.map((fetchedMsg) => {
      const refMsg = lastCacheUpdateRef.current.get(fetchedMsg.id)
      // If ref has reactions, prefer them (they might be more recent from optimistic updates)
      if (refMsg?.reactions && refMsg.reactions.length > 0) {
        // Use ref reactions if fetched doesn't have them, or if ref has more
        if (!fetchedMsg.reactions || fetchedMsg.reactions.length === 0) {
          return { ...fetchedMsg, reactions: refMsg.reactions }
        }
        // If both have reactions, use the one with more (likely more up-to-date from optimistic update)
        if (refMsg.reactions.length >= fetchedMsg.reactions.length) {
          return { ...fetchedMsg, reactions: refMsg.reactions }
        }
      }
      // Update ref with fetched message if it doesn't exist in ref or has reactions
      if (!refMsg || (fetchedMsg.reactions && fetchedMsg.reactions.length > 0)) {
        lastCacheUpdateRef.current.set(fetchedMsg.id, fetchedMsg)
      }
      return fetchedMsg
    })
    
    return mergedMessages
  }, [queryResult.data, conversation.type, conversation.userId])
  
  const isLoading = queryResult.isLoading

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const teamMembers = users.filter((u) => u.id !== user?.id && u.isActive)
  const [directSearch, setDirectSearch] = useState('')
  const [directListExpanded, setDirectListExpanded] = useState(false)

  const filteredTeamMembers = useMemo(() => {
    if (!directSearch.trim()) return teamMembers
    const term = directSearch.toLowerCase().trim()
    return teamMembers.filter(
      (m) =>
        formatUserName(m).toLowerCase().includes(term) ||
        m.email?.toLowerCase().includes(term) ||
        m.username?.toLowerCase().includes(term)
    )
  }, [teamMembers, directSearch])

  const sendMutation = useMutation({
    mutationFn: chatService.sendMessage,
    onMutate: async (newMessage) => {
      const queryKey = ['chat-messages', conversation.type, conversation.userId]
      await queryClient.cancelQueries({ queryKey })

      const previousMessages = queryClient.getQueryData<Message[]>(queryKey)

      if (previousMessages && user) {
        const recipientId = newMessage.recipientId
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          content: newMessage.content,
          senderId: user.id,
          recipientId,
          createdAt: new Date().toISOString(),
          isTeamMessage: !recipientId,
          readAt: undefined,
        }
        queryClient.setQueryData<Message[]>(queryKey, (old = []) => [...old, optimisticMessage])
      }

      return { previousMessages, queryKey }
    },
    onError: (_err, _newMessage, context) => {
      if (context?.previousMessages && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousMessages)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  const updateMessageMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
      chatService.updateMessage(messageId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
    },
  })

  const deleteMessageMutation = useMutation({
    mutationFn: chatService.deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
    },
  })

  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatService.addReaction(messageId, emoji),
    onMutate: async ({ messageId, emoji }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages', conversation.type, conversation.userId] })

      const previousMessages = queryClient.getQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId])

      // Optimistically update the message with the new reaction
      if (previousMessages && user) {
        const updatedMessages = previousMessages.map((msg) => {
          if (msg.id === messageId) {
            const reactions = msg.reactions || []
            // Remove existing reaction from this user for this emoji
            const filteredReactions = reactions.filter(
              (r) => !(r.userId === user.id && r.emoji === emoji)
            )
            // Add new reaction
            filteredReactions.push({
              emoji,
              userId: user.id,
              createdAt: new Date().toISOString(),
            })
            return { ...msg, reactions: filteredReactions }
          }
          return msg
        })
        queryClient.setQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId], updatedMessages)
      }

      return { previousMessages }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages', conversation.type, conversation.userId], context.previousMessages)
      }
      console.error('Failed to add reaction:', error)
    },
    onSuccess: async (updatedMessage) => {
      console.log('Add reaction success, updated message:', updatedMessage, 'reactions:', updatedMessage.reactions)
      // Cancel any pending refetches to prevent overwriting our update
      await queryClient.cancelQueries({ queryKey: ['chat-messages', conversation.type, conversation.userId] })
      
      // Update the ref with the latest state FIRST
      lastCacheUpdateRef.current.set(updatedMessage.id, updatedMessage)
      
      // Update the cache with the server response - use the full updated message from server
      queryClient.setQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            // Use the server response which should have the latest reactions
            return updatedMessage
          }
          return msg
        })
        console.log('Updated cache for conversation', conversation.type, 'message reactions:', updated.find(m => m.id === updatedMessage.id)?.reactions)
        return updated
      })
      // Also update other conversation caches that might contain this message
      const otherKeys: Array<[ConversationType, string | undefined]> = [
        [conversation.type === 'team' ? 'all' : 'team', undefined],
        ...(conversation.type === 'direct' ? [['all', undefined] as [ConversationType, undefined]] : []),
      ]
      otherKeys.forEach(([type, uid]) => {
        queryClient.setQueryData<Message[]>(['chat-messages', type, uid], (old = []) => {
          const updated = old.map((msg) =>
            msg.id === updatedMessage.id ? updatedMessage : msg
          )
          updated.forEach((msg) => lastCacheUpdateRef.current.set(msg.id, msg))
          return updated
        })
      })
    },
  })

  const removeReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatService.removeReaction({ messageId, emoji }),
    onMutate: async ({ messageId, emoji }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages', conversation.type, conversation.userId] })

      const previousMessages = queryClient.getQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId])

      // Optimistically update the message by removing the reaction
      if (previousMessages && user) {
        const updatedMessages = previousMessages.map((msg) => {
          if (msg.id === messageId) {
            const reactions = (msg.reactions || []).filter(
              (r) => !(r.userId === user.id && r.emoji === emoji)
            )
            return { ...msg, reactions }
          }
          return msg
        })
        queryClient.setQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId], updatedMessages)
      }

      return { previousMessages }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages', conversation.type, conversation.userId], context.previousMessages)
      }
      console.error('Failed to remove reaction:', error)
    },
    onSuccess: async (updatedMessage) => {
      console.log('Remove reaction success, updated message:', updatedMessage, 'reactions:', updatedMessage.reactions)
      // Cancel any pending refetches to prevent overwriting our update
      await queryClient.cancelQueries({ queryKey: ['chat-messages', conversation.type, conversation.userId] })
      
      // Update the ref with the latest state
      lastCacheUpdateRef.current.set(updatedMessage.id, updatedMessage)
      
      // Update the cache with the server response - use the full updated message from server
      queryClient.setQueryData<Message[]>(['chat-messages', conversation.type, conversation.userId], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            // Use the server response which should have the latest reactions
            return updatedMessage
          }
          return msg
        })
        console.log('Updated cache for conversation', conversation.type, 'message reactions:', updated.find(m => m.id === updatedMessage.id)?.reactions)
        return updated
      })
      // Also update other conversation caches that might contain this message
      const otherKeys: Array<[ConversationType, string | undefined]> = [
        [conversation.type === 'team' ? 'all' : 'team', undefined],
        ...(conversation.type === 'direct' ? [['all', undefined] as [ConversationType, undefined]] : []),
      ]
      otherKeys.forEach(([type, uid]) => {
        queryClient.setQueryData<Message[]>(['chat-messages', type, uid], (old = []) => {
          const updated = old.map((msg) =>
            msg.id === updatedMessage.id ? updatedMessage : msg
          )
          updated.forEach((msg) => lastCacheUpdateRef.current.set(msg.id, msg))
          return updated
        })
      })
    },
  })

  const markReadMutation = useMutation({
    mutationFn: chatService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mark messages as read when viewing
  useEffect(() => {
    if (messages.length > 0 && user) {
      const unreadMessages = messages.filter((msg) => {
        if (msg.senderId === user.id) return false
        if (msg.isTeamMessage) {
          const readBy = Array.isArray(msg.readBy) ? msg.readBy : []
          return !readBy.includes(user.id)
        }
        return msg.recipientId === user.id && !msg.readAt
      })
      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map((msg) => msg.id)
        markReadMutation.mutate(messageIds)
      }
    }
  }, [messages, user])

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !user) return

    sendMutation.mutate({
      content: content.trim(),
      recipientId: conversation.type === 'direct' && conversation.userId ? conversation.userId : undefined,
    })
  }

  const handleEditMessage = (messageId: string, newContent: string) => {
    updateMessageMutation.mutate({ messageId, content: newContent })
  }

  const handleDeleteMessage = (messageId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessageMutation.mutate(messageId)
    }
  }

  const handleAddReaction = (messageId: string, emoji: string) => {
    console.log('handleAddReaction called:', { messageId, emoji })
    addReactionMutation.mutate({ messageId, emoji })
  }

  const handleRemoveReaction = (messageId: string, emoji: string) => {
    removeReactionMutation.mutate({ messageId, emoji })
  }

  const filteredMessages = messages.filter((msg) => {
    if (conversation.type === 'team') {
      return msg.isTeamMessage
    }
    if (conversation.type === 'direct' && conversation.userId) {
      // getDirectMessages already returns only messages between the two users
      return true
    }
    return true
  })

  const groupedMessages = groupMessagesByDate(filteredMessages)
  const directChatUser =
    conversation.type === 'direct' && conversation.userId
      ? users.find((u) => u.id === conversation.userId)
      : null

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <div className="chat-conversation-list">
          <button
            type="button"
            className={`chat-conversation-item ${conversation.type === 'team' ? 'active' : ''}`}
            onClick={() => {
              setDirectSearch('')
              setDirectListExpanded(false)
              setConversation({ type: 'team' })
            }}
          >
            <span className="chat-conversation-icon">#</span>
            <span>Team Chat</span>
          </button>
          <button
            type="button"
            className={`chat-conversation-item ${conversation.type === 'all' ? 'active' : ''}`}
            onClick={() => {
              setDirectSearch('')
              setDirectListExpanded(false)
              setConversation({ type: 'all' })
            }}
          >
            <span className="chat-conversation-icon">@</span>
            <span>All Messages</span>
          </button>
          {teamMembers.length > 0 && (
            <>
              <button
                type="button"
                className={`chat-conversation-divider-button ${directListExpanded || conversation.type === 'direct' ? 'expanded' : ''}`}
                onClick={() => setDirectListExpanded((prev) => !prev)}
              >
                Direct messages
                <span className="chat-conversation-divider-chevron">
                  {directListExpanded || conversation.type === 'direct' ? '▼' : '▶'}
                </span>
              </button>
              {(directListExpanded || conversation.type === 'direct') && (
                <>
                  <input
                    type="search"
                    className="chat-conversation-search"
                    placeholder="Search people..."
                    value={directSearch}
                    onChange={(e) => setDirectSearch(e.target.value)}
                    aria-label="Search team members"
                  />
                  {filteredTeamMembers.length === 0 ? (
                    <div className="chat-conversation-empty">No matching team members</div>
                  ) : (
                    filteredTeamMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        className={`chat-conversation-item chat-conversation-item-user ${conversation.type === 'direct' && conversation.userId === member.id ? 'active' : ''}`}
                        onClick={() => {
                    setDirectSearch('')
                    setConversation({ type: 'direct', userId: member.id })
                  }}
                      >
                        <Avatar user={member} size="small" className="chat-conversation-avatar" />
                        <span className="chat-conversation-name">{formatUserName(member)}</span>
                      </button>
                    ))
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {directChatUser && (
        <div className="chat-conversation-header">
          <Avatar user={directChatUser} size="small" className="chat-conversation-header-avatar" />
          <span className="chat-conversation-header-title">
            Chat with {formatUserName(directChatUser)}
          </span>
        </div>
      )}

      <div className="chat-messages">
        {isLoading && messages.length === 0 ? (
          <div className="chat-list-loading">Loading messages...</div>
        ) : filteredMessages.length === 0 ? (
          <div className="chat-empty">No messages yet. Start the conversation!</div>
        ) : (
          groupedMessages.map((group) => (
            <div key={group.dateKey}>
              <div className="chat-date-separator">
                <span className="chat-date-separator-label">{group.dateLabel}</span>
              </div>
              {group.messages.map((message) => {
                const isOwnMessage = message.senderId === user?.id
                const sender = users.find((u) => u.id === message.senderId)
                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    sender={sender}
                    currentUserId={user?.id}
                    isOwnMessage={isOwnMessage}
                    allUsers={users}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onAddReaction={handleAddReaction}
                    onRemoveReaction={handleRemoveReaction}
                  />
                )
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput onSend={handleSendMessage} disabled={sendMutation.isPending} />
    </div>
  )
}














